import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb, { BreadcrumbSchema } from '@/components/feature/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

const categoryLabels: Record<string, string> = {
  beginner: '新手入门',
  character: '角色攻略',
  hidden: '隐藏内容',
  advanced: '进阶技巧',
  resource: '资源大全',
};

const detailImageMap: Record<string, string> = {
  'article-beginner-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20beginner%20gaming%20guide%20article%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20soft%20ambient%20lighting%2C%20decorative%20game%20elements%20on%20cream%20background&width=1200&height=600&seq=article-beginner-01-detail&orientation=landscape',
  'article-character-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20character%20gaming%20guide%20article%2C%20warm%20coral%20and%20golden%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20character%20lineup%20on%20cream%20background&width=1200&height=600&seq=article-character-01-detail&orientation=landscape',
  'article-hidden-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20hidden%20secrets%20gaming%20guide%20article%2C%20warm%20golden%20glow%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20mysterious%20elements%20on%20cream%20background&width=1200&height=600&seq=article-hidden-01-detail&orientation=landscape',
  'article-advanced-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20advanced%20strategy%20gaming%20guide%20article%2C%20warm%20olive%20and%20golden%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20tactical%20elements%20on%20cream%20background&width=1200&height=600&seq=article-advanced-01-detail&orientation=landscape',
  'article-resource-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20resource%20farming%20gaming%20guide%20article%2C%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20coins%20and%20gems%20on%20cream%20background&width=1200&height=600&seq=article-resource-01-detail&orientation=landscape',
  'article-daily-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20daily%20checklist%20gaming%20guide%20article%2C%20warm%20amber%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20calendar%20elements%20on%20cream%20background&width=1200&height=600&seq=article-daily-01-detail&orientation=landscape',
  'article-team-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20team%20building%20gaming%20guide%20article%2C%20warm%20olive%20and%20golden%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20strategy%20formation%20on%20cream%20background&width=1200&height=600&seq=article-team-01-detail&orientation=landscape',
  'article-equip-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20equipment%20upgrade%20gaming%20guide%20article%2C%20warm%20golden%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20weapons%20and%20armor%20on%20cream%20background&width=1200&height=600&seq=article-equip-01-detail&orientation=landscape',
  'article-event-01': 'https://readdy.ai/api/search-image?query=Stylized%20flat%20vector%20illustration%20for%20event%20calendar%20gaming%20guide%20article%2C%20warm%20coral%20and%20golden%20tones%2C%20minimalist%20design%2C%20wide%20composition%2C%20celebration%20elements%20on%20cream%20background&width=1200&height=600&seq=article-event-01-detail&orientation=landscape',
};

function ArticleSchema({ article }: { article: typeof articles[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '黄油游戏官方指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '黄油游戏官方指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://butter-game-guide.readdy.ai/article/${article.id}`,
    },
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黄油游戏官方指南`;
    } else {
      document.title = '攻略未找到 - 黄油游戏官方指南';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-3">攻略未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您查找的攻略文章不存在或已被移除。</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回攻略列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel = categoryLabels[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleSchema article={article} />
      <Navbar />
      <ScrollToTopButton />

      <main className="pt-20">
        <article className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('category.title'), path: '/category/all' },
                { label: categoryLabel, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <BreadcrumbSchema
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('category.title'), path: '/category/all' },
                { label: categoryLabel, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {categoryLabel}
                </span>
                {article.featured && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                    精选攻略
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-6 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间约 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line"></i>
                  {article.tags.join('、')}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full rounded-xl overflow-hidden bg-background-200 mb-10">
              <img
                src={detailImageMap[article.imageSeq] || detailImageMap['article-beginner-01']}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div
              className="prose max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-12 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Back link */}
            <div className="mb-12">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article.backToList')}
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
                {t('article.related')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}